<div class="min-h-screen bg-gray-900">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <!-- Header -->
    <div class="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-white">Dashboard</h1>
        <p class="mt-1 text-sm text-gray-400">
          Overzicht van recente activiteit
          <span *ngIf="selectedDomain" class="text-indigo-400 font-medium">· {{ selectedDomain }}</span>
          <span *ngIf="!selectedDomain" class="text-gray-500">· alle domeinen</span>
        </p>
      </div>
      <div class="flex items-center gap-2">
        <label for="domainFilter" class="text-xs text-gray-400 font-medium uppercase tracking-wider">Domein</label>
        <select
          id="domainFilter"
          [(ngModel)]="selectedDomain"
          (ngModelChange)="onDomainChange()"
          class="bg-gray-800 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500">
          <option value="">Alle domeinen</option>
          <option *ngFor="let d of domains" [value]="d.name">{{ d.name }}</option>
        </select>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <div class="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4">
        <div class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-2">Bestellingen vandaag</div>
        <div class="text-2xl font-semibold text-white">{{ todayOrderCount }}</div>
      </div>

      <div class="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4">
        <div class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-2">Omzet deze maand</div>
        <div class="text-2xl font-semibold text-white">{{ formatCurrency(monthRevenue) }}</div>
      </div>

      <div class="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4">
        <div class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-2">Berichten deze week</div>
        <div class="text-2xl font-semibold text-white">{{ weekSubmissionCount }}</div>
      </div>
    </div>

    <!-- 30-day chart -->
    <div class="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-8">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-base font-semibold text-white">Deze maand</h2>
          <p class="text-xs text-gray-500 mt-0.5">
            Bestellingen per dag
            <span *ngIf="selectedDomain"> · {{ selectedDomain }}</span>
            <span *ngIf="!selectedDomain"> · alle domeinen</span>
          </p>
        </div>
      </div>

      <!-- Summary stats from chart data -->
      <div class="grid grid-cols-2 gap-4 mb-5">
        <div>
          <div class="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Bestellingen</div>
          <div class="text-lg font-semibold text-white">{{ chartTotalOrders }}</div>
        </div>
        <div>
          <div class="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Omzet</div>
          <div class="text-lg font-semibold text-white">{{ formatCurrency(chartTotalRevenue) }}</div>
        </div>
      </div>

      <!-- Bar chart -->
      <div *ngIf="!loadingChart && chartReports.length > 0" class="relative">
        <!-- Y-axis labels (left) + gridlines (full width) -->
        <div class="relative h-40 ml-8">
          <!-- Gridlines + Y labels -->
          <div *ngFor="let tick of chartYTicks; let i = index"
               class="absolute left-0 right-0 border-t border-gray-700/40"
               [style.top.%]="(i / (chartYTicks.length - 1)) * 100">
            <span class="absolute -left-8 -translate-y-1/2 text-[10px] text-gray-500 tabular-nums">{{ tick }}</span>
          </div>

          <!-- Bars -->
          <div class="absolute inset-0 flex items-end gap-0.5">
            <div *ngFor="let r of chartReports"
                 class="group relative flex-1 h-full flex items-end">
              <!-- Hover tooltip (only when bar is hovered) -->
              <div class="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-[10px] text-gray-200 whitespace-nowrap pointer-events-none z-10">
                {{ chartHoverLabel(r) }}
              </div>
              <!-- Bar itself -->
              <div class="w-full transition-all rounded-t-sm cursor-pointer"
                   [style.background-color]="r.exists ? 'rgba(99, 102, 241, 0.7)' : 'rgba(55, 65, 81, 0.4)'"
                   [style.height.%]="chartBarHeight(r) || (r.exists ? 2 : 4)"
                   [class.hover:opacity-100]="true">
              </div>
            </div>
          </div>
        </div>

        <!-- X-axis labels: day-of-month every ~5 days -->
        <div class="ml-8 mt-2 flex gap-0.5">
          <div *ngFor="let r of chartReports; let i = index"
               class="flex-1 text-center text-[9px] tabular-nums"
               [class.text-gray-500]="i % 5 === 0 || i === chartReports.length - 1"
               [class.text-transparent]="!(i % 5 === 0 || i === chartReports.length - 1)">
            {{ chartShortDate(r) }}
          </div>
        </div>
      </div>

      <div *ngIf="loadingChart" class="text-xs text-gray-500 py-4 text-center">Chart laden...</div>
      <div *ngIf="!loadingChart && chartMaxOrders === 0" class="text-xs text-gray-500 py-4 text-center">
        Nog geen rapport-data voor deze maand.
      </div>
    </div>

    <!-- Backfill rapporten per week (admin-only) -->
    <div *ngIf="canBackfill" class="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-8">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div>
          <h2 class="text-base font-semibold text-white">Rapport-backfill</h2>
          <p class="text-xs text-gray-500 mt-0.5">Genereer dagelijkse rapporten voor 7 dagen tegelijk</p>
        </div>
        <div class="flex items-center gap-2">
          <button
            (click)="prevWeek()"
            class="p-1.5 text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded-md transition-colors"
            title="Week eerder">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div class="text-sm text-gray-200 font-medium min-w-[160px] text-center">
            {{ weekRangeLabel }}
          </div>
          <button
            (click)="nextWeek()"
            [disabled]="weekOffset === 0"
            class="p-1.5 text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Week later">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            (click)="runBackfill()"
            [disabled]="backfilling"
            class="ml-2 text-xs text-white bg-indigo-600 hover:bg-indigo-500 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50">
            {{ backfilling ? 'Bezig...' : 'Genereer deze week' }}
          </button>
        </div>
      </div>
      <p *ngIf="backfillMessage" class="text-xs text-indigo-400 mt-2">{{ backfillMessage }}</p>

      <!-- Debug trace from the Cloud Function -->
      <div *ngIf="backfillTrace.length > 0" class="mt-3 bg-gray-900 border border-gray-700 rounded-md p-3">
        <div class="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Run-log</div>
        <pre class="text-[11px] text-gray-300 font-mono whitespace-pre-wrap leading-relaxed">{{ backfillTrace.join('\n') }}</pre>
      </div>
    </div>

    <!-- Two-column layout -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

      <!-- Recent Orders -->
      <div class="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 class="text-base font-semibold text-white">Laatste bestellingen</h2>
        </div>

        <div *ngIf="loadingOrders" class="px-5 py-12 text-center text-sm text-gray-500">Laden...</div>

        <div *ngIf="!loadingOrders && orders.length === 0" class="px-5 py-12 text-center text-sm text-gray-500">
          Nog geen bestellingen
        </div>

        <div *ngIf="!loadingOrders && orders.length > 0" class="divide-y divide-gray-700/50">
          <div *ngFor="let order of orders" class="px-5 py-3 hover:bg-gray-700/30 transition-colors">
            <div class="flex items-center justify-between gap-3">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-sm font-medium text-gray-200 truncate">{{ order.customerName }}</span>
                  <span class="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-medium"
                    [ngClass]="statusColor(order.status)">
                    {{ statusLabel(order.status) }}
                  </span>
                </div>
                <div class="flex items-center gap-2 text-xs text-gray-500">
                  <span>#{{ order.number }}</span>
                  <span>·</span>
                  <span>{{ order.itemCount }} item{{ order.itemCount === 1 ? '' : 's' }}</span>
                  <span>·</span>
                  <span>{{ formatDate(order.dateCreated) }}</span>
                </div>
              </div>
              <div class="text-right flex-shrink-0">
                <div class="text-sm font-semibold text-white">{{ formatCurrency(order.total, order.currency) }}</div>
                <a *ngIf="order.customerEmail"
                  [href]="'mailto:' + order.customerEmail"
                  class="text-[11px] text-gray-500 hover:text-indigo-400 transition-colors truncate block max-w-[180px]">
                  {{ order.customerEmail }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Submissions -->
      <div class="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 class="text-base font-semibold text-white">Ingestuurde berichten</h2>
        </div>

        <div *ngIf="loadingSubmissions" class="px-5 py-12 text-center text-sm text-gray-500">Laden...</div>

        <div *ngIf="!loadingSubmissions && submissions.length === 0" class="px-5 py-12 text-center text-sm text-gray-500">
          Nog geen inzendingen
        </div>

        <div *ngIf="!loadingSubmissions && submissions.length > 0" class="divide-y divide-gray-700/50">
          <div *ngFor="let sub of submissions" class="px-5 py-3 hover:bg-gray-700/30 transition-colors">
            <div class="flex items-start justify-between gap-3 mb-1">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-gray-200 truncate">{{ sub.name || sub.email || '(anoniem)' }}</span>
                  <span *ngIf="sub.domain" class="text-[10px] text-gray-500 font-mono">{{ sub.domain }}</span>
                </div>
                <p class="text-xs text-gray-400 mt-1 line-clamp-2">{{ sub.preview }}</p>
              </div>
              <span class="text-[11px] text-gray-500 flex-shrink-0 whitespace-nowrap">{{ formatDate(sub.submittedAt) }}</span>
            </div>
            <a *ngIf="sub.email"
              [href]="'mailto:' + sub.email"
              class="text-[11px] text-indigo-400 hover:text-indigo-300 transition-colors">
              {{ sub.email }}
            </a>
          </div>
        </div>
      </div>

    </div>
  </div>
</div>
